/**
 * Générateur — Réaliser des mesures et les consigner dans un tableau
 * Attendu ID : 676 | Niveau : 6ème | Domaine : Données et probabilités
 *
 * Types générés :
 *   Template (lecture_tableau_mesures) : qcm_unique, texte, relier, tableau_vf, texte_trou_drag, texte_trou_saisie
 *
 * Labels de référence :
 *   - Lire et extraire des valeurs d'un tableau de mesures
 *   - Identifier la plus grande / plus petite valeur
 *   - Comparer des mesures dans un tableau
 *   - Calculer des écarts entre mesures
 *
 * Variantes estimées : 500+ (objectif : 20/20 uniques sur 20 itérations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 676;

const NOMS = [
    'Léa', 'Hugo', 'Emma', 'Lucas', 'Chloé', 'Nathan',
    'Jade', 'Louis', 'Manon', 'Théo', 'Inès', 'Raphaël',
    'Zoé', 'Adam', 'Lina', 'Gabriel',
];

const LABEL = 'Lire et extraire des valeurs d\'un tableau de mesures';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || LABEL,
});

// ─── Contextes de tableaux ──────────────────────────────────

const buildContext = () => {
    const type = randomChoice(['meteo', 'sport', 'plante', 'tailles', 'trajets']);

    if (type === 'meteo') {
        const jours = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
        const cols = ['Température (°C)', 'Précipitations (mm)'];
        const data = jours.map(j => ({
            row: j,
            vals: [randInt(5, 30), randInt(0, 15)],
        }));
        return {
            name: 'Relevés météo de la semaine',
            row_header: 'Jour',
            col_headers: cols,
            units: ['°C', 'mm'],
            grandeurs: ['température', 'quantité de pluie'],
            genres: ['f', 'f'],
            rows: data,
        };
    }

    if (type === 'sport') {
        const eleves = randomChoiceMultiple(NOMS, 5);
        const cols = ['Temps (min)', 'Distance (m)'];
        const data = eleves.map(e => ({
            row: e,
            vals: [randInt(8, 25), randInt(100, 2000)],
        }));
        return {
            name: 'Résultats sportifs des élèves',
            row_header: 'Élève',
            col_headers: cols,
            units: ['min', 'm'],
            grandeurs: ['temps', 'distance'],
            genres: ['m', 'f'],
            rows: data,
        };
    }

    if (type === 'plante') {
        const semaines = ['Semaine 1', 'Semaine 2', 'Semaine 3', 'Semaine 4', 'Semaine 5'];
        const cols = ['Hauteur (cm)'];
        let h = randInt(3, 8);
        const data = semaines.map(s => {
            const row = { row: s, vals: [h] };
            h += randInt(1, 4);
            return row;
        });
        return {
            name: 'Croissance d\'une plante',
            row_header: 'Période',
            col_headers: cols,
            units: ['cm'],
            grandeurs: ['hauteur'],
            genres: ['f'],
            rows: data,
        };
    }

    if (type === 'tailles') {
        const eleves = randomChoiceMultiple(NOMS, 5);
        const cols = ['Septembre (cm)', 'Juin (cm)'];
        const data = eleves.map(e => {
            const sept = randInt(130, 165);
            return { row: e, vals: [sept, sept + randInt(1, 5)] };
        });
        return {
            // « scolaire » (revue 2026-08-08) : sans lui, juin lisait comme le
            // milieu de l'année (civile).
            name: 'Tailles des élèves en début et fin d\'année scolaire',
            row_header: 'Élève',
            col_headers: cols,
            units: ['cm', 'cm'],
            grandeurs: ['taille en septembre', 'taille en juin'],
            genres: ['f', 'f'],
            rows: data,
        };
    }

    // trajets
    const lieux = ['Maison-École', 'École-Stade', 'Maison-Piscine', 'École-Bibliothèque'];
    const cols = ['Distance (km)', 'Durée (min)'];
    const data = lieux.map(l => {
        const dist = randInt(1, 12);
        return { row: l, vals: [dist, dist * randInt(3, 8)] };
    });
    return {
        name: 'Trajets et durées',
        row_header: 'Trajet',
        col_headers: cols,
        units: ['km', 'min'],
        grandeurs: ['distance', 'durée'],
        genres: ['f', 'f'],
        rows: data,
    };
};

// ─── HTML du tableau ─────────────────────────────────────────

// Titre du tableau dans l'intro (revue 2026-08-08) : chaque contexte porte un
// nom qui n'était jamais affiché — un tableau de mesures se titre.
const intro = (ctx) => `<p>Observe le tableau ci-dessous : <b>${ctx.name}</b>.</p>`;

const tableHTML = (ctx) => {
    let html = '<table border="1" cellpadding="5" cellspacing="0">';
    html += `<tr><th>${ctx.row_header}</th>`;
    ctx.col_headers.forEach(c => { html += `<th>${c}</th>`; });
    html += '</tr>';
    ctx.rows.forEach(r => {
        html += `<tr><td><b>${r.row}</b></td>`;
        r.vals.forEach(v => { html += `<td>${v}</td>`; });
        html += '</tr>';
    });
    html += '</table>';
    return html;
};

// ─── Helpers ─────────────────────────────────────────────────

const pickCol = (ctx) => {
    const colIdx = randInt(0, ctx.col_headers.length - 1);
    return colIdx;
};

const maxRow = (ctx, colIdx) => {
    let best = ctx.rows[0];
    ctx.rows.forEach(r => { if (r.vals[colIdx] > best.vals[colIdx]) best = r; });
    return best;
};

const minRow = (ctx, colIdx) => {
    let best = ctx.rows[0];
    ctx.rows.forEach(r => { if (r.vals[colIdx] < best.vals[colIdx]) best = r; });
    return best;
};

/** Retourne { grandeur, genre } pour la colonne d'index colIdx du contexte */
const getGrandeur = (ctx, colIdx) => ({
    grandeur: ctx.grandeurs[colIdx],
    genre: ctx.genres[colIdx],
});

// Helpers d'accord grammatical basés sur le genre explicite
const articleDef = (genre) => genre === 'm' ? 'Le' : 'La';
const articleDefMin = (genre) => genre === 'm' ? 'le' : 'la';
const plusEleve = (genre) => genre === 'm' ? 'le plus élevé' : 'la plus élevée';
const observe = (genre) => genre === 'm' ? 'observé' : 'observée';
const superieur = (genre) => genre === 'm' ? 'supérieur' : 'supérieure';
const inferieur = (genre) => genre === 'm' ? 'inférieur' : 'inférieure';
const quel = (genre) => genre === 'm' ? 'Quel' : 'Quelle';
const sonSa = (genre) => genre === 'm' ? 'son' : 'sa';
const celuiCelle = (genre) => genre === 'm' ? 'celui' : 'celle';
const ilElle = (genre) => genre === 'm' ? 'il' : 'elle';

// ─── Générateurs par format ──────────────────────────────────

// QCM unique
const genQcm = () => {
    const ctx = buildContext();
    const table = tableHTML(ctx);
    const variant = randomChoice(['lecture', 'max_min', 'comparaison']);

    if (variant === 'lecture') {
        const colIdx = pickCol(ctx);
        const target = randomChoice(ctx.rows);
        const correct = String(target.vals[colIdx]);
        const { grandeur, genre } = getGrandeur(ctx, colIdx);
        const unit = ctx.units[colIdx];
        const texte = `${intro(ctx)}${table}<p>Quel${genre === 'm' ? '' : 'le'} est ${articleDefMin(genre)} ${grandeur} de <b>${target.row}</b> ?</p>`;

        // Distracteurs : autres cellules de la même colonne + off-by
        const others = ctx.rows.filter(r => r !== target).map(r => String(r.vals[colIdx]));
        const offBy = [String(target.vals[colIdx] + randInt(1, 3)), String(Math.max(0, target.vals[colIdx] - randInt(1, 3)))];
        const pool = [...new Set([...others, ...offBy])].filter(v => v !== correct);
        const distracteurs = shuffle(pool).slice(0, 3);
        const choix = [correct, ...distracteurs].map(v => `${v} ${unit}`);
        const correctLabel = `${correct} ${unit}`;

        const payload = makeQcm(choix, correctLabel, texte, opts());
        payload._meta.generatorKey = 'lecture_tableau_mesures';
        return payload;
    }

    if (variant === 'max_min') {
        // Revue 2026-08-08 : extremum UNIQUE garanti (11,5 % des tirages
        // avaient deux gagnants ex aequo — « Lundi 5 mm et Vendredi 5 mm »,
        // l'autre gagnant comptait faux) ; contexte plante exclu (croissance
        // monotone → réponse constante Semaine 1/5, et « Quel période » fautif).
        const isMax = randomChoice([true, false]);
        const extremumUnique = (cc, ci) => {
            const vals = cc.rows.map(r => r.vals[ci]);
            const cible = isMax ? Math.max(...vals) : Math.min(...vals);
            return vals.filter(v => v === cible).length === 1;
        };
        let c2 = ctx;
        let colIdx = pickCol(c2);
        for (let tries = 0; (c2.row_header === 'Période' || !extremumUnique(c2, colIdx)) && tries < 50; tries++) {
            c2 = buildContext();
            colIdx = pickCol(c2);
        }
        if (c2.row_header === 'Période' || !extremumUnique(c2, colIdx)) return genQcm();
        const table2 = tableHTML(c2);
        const bestRow = isMax ? maxRow(c2, colIdx) : minRow(c2, colIdx);
        const { grandeur, genre } = getGrandeur(c2, colIdx);
        const adj = isMax
            ? (genre === 'm' ? 'le plus grand' : 'la plus grande')
            : (genre === 'm' ? 'le plus petit' : 'la plus petite');
        const texte = `${intro(c2)}${table2}<p>Quel ${c2.row_header.toLowerCase()} a ${adj} ${grandeur} ?</p>`;

        const correct = bestRow.row;
        const distracteurs = c2.rows.filter(r => r !== bestRow).map(r => r.row);
        const choix = [correct, ...shuffle(distracteurs).slice(0, 3)];

        const payload = makeQcm(choix, correct, texte, opts());
        payload._meta.generatorKey = 'lecture_tableau_mesures';
        return payload;
    }

    // comparaison — refonte (revue 2026-08-08). Avant : égalité possible
    // (bonne réponse « ce n'est pas vrai : 14 ≤ 14 » fausse + distracteur
    // « identiques » juste), choix « Oui, car 5 ≤ 12 » incohérent dans 52 %
    // des tirages, accords faux sur les grandeurs masculines. Désormais :
    // valeurs garanties distinctes, 4 choix tous cohérents en interne, les
    // leurres portent les valeurs de la mauvaise colonne / d'autres lignes
    // (doctrine des leurres du relier, bilan prof 2026-07-27).
    const colIdx = pickCol(ctx);
    let item1, item2;
    let tries = 0;
    do {
        [item1, item2] = randomChoiceMultiple(ctx.rows, 2);
        tries++;
    } while (item1.vals[colIdx] === item2.vals[colIdx] && tries < 30);
    if (item1.vals[colIdx] === item2.vals[colIdx]) return genQcm();
    const v1 = item1.vals[colIdx];
    const v2 = item2.vals[colIdx];
    const { grandeur, genre } = getGrandeur(ctx, colIdx);
    const estSup = v1 > v2;
    const texte = `${intro(ctx)}${table}<p>${articleDef(genre)} ${grandeur} de <b>${item1.row}</b> est-${genre === 'm' ? 'il supérieur' : 'elle supérieure'} à ${genre === 'm' ? 'celui' : 'celle'} de <b>${item2.row}</b> ?</p>`;

    const bonne = estSup ? `Oui, car ${v1} > ${v2}` : `Non, car ${v1} < ${v2}`;
    const enonces = new Set([bonne]);
    const leurres = [];
    const pushLeurre = (x, y) => {
        if (x === y) return;
        const s = x > y ? `Oui, car ${x} > ${y}` : `Non, car ${x} < ${y}`;
        if (enonces.has(s)) return;
        enonces.add(s);
        leurres.push(s);
    };
    // Mauvaise colonne, dans les deux sens de lecture (décision utilisateur
    // 2026-08-08 : les leurres se construisent avec les nombres du tableau,
    // pas avec des valeurs inventées)
    const colAutre = ctx.col_headers.length > 1 ? (colIdx === 0 ? 1 : 0) : null;
    if (colAutre !== null) {
        pushLeurre(item1.vals[colAutre], item2.vals[colAutre]);
        pushLeurre(item2.vals[colAutre], item1.vals[colAutre]);
    }
    // Lecture en ligne (décision utilisateur 2026-08-08) : les deux valeurs
    // d'une MÊME ligne de la question — l'élève qui a trouvé la bonne entrée
    // mais compare les nombres côte à côte au lieu de descendre la colonne.
    if (colAutre !== null && leurres.length < 3) {
        const ligne = randomChoice([item1, item2]);
        pushLeurre(ligne.vals[colIdx], ligne.vals[colAutre]);
        if (leurres.length < 3) pushLeurre(ligne.vals[colAutre], ligne.vals[colIdx]);
    }
    // Mauvaises lignes : d'autres paires, bonne colonne
    const autres = shuffle(ctx.rows.filter(r => r !== item1 && r !== item2));
    for (let k = 0; k + 1 < autres.length && leurres.length < 3; k++) {
        pushLeurre(autres[k].vals[colIdx], autres[k + 1].vals[colIdx]);
    }
    // Repli : l'affirmation inverse (forme cohérente, maths fausses)
    if (leurres.length < 3) {
        const inverse = estSup ? `Non, car ${v1} < ${v2}` : `Oui, car ${v1} > ${v2}`;
        if (!enonces.has(inverse)) leurres.push(inverse);
    }
    // Tout dernier recours (contexte à 1 colonne, pools épuisés) : valeurs proches
    for (let g = 0; leurres.length < 3 && g < 20; g++) {
        pushLeurre(Math.max(0, v1 + randInt(1, 4) * randomChoice([-1, 1])), v2);
    }
    const choix = [bonne, ...leurres.slice(0, 3)];
    const payload = makeQcm(choix, bonne, texte, opts());
    payload._meta.generatorKey = 'lecture_tableau_mesures';
    return payload;
};

// Texte (saisie)
const genSaisie = () => {
    const ctx = buildContext();
    const table = tableHTML(ctx);
    const colIdx = pickCol(ctx);
    const target = randomChoice(ctx.rows);
    const correct = String(target.vals[colIdx]);
    const { grandeur, genre } = getGrandeur(ctx, colIdx);
    const unit = ctx.units[colIdx];

    const texte = `${intro(ctx)}${table}<p>Quel${genre === 'm' ? '' : 'le'} est ${articleDefMin(genre)} ${grandeur} de <b>${target.row}</b> d'après le tableau ?</p>`;
    // Pas de strict (revue 2026-08-08) : on teste une lecture, la comparaison
    // par valeur suffit (21 = 21,0 accepté).
    const payload = makeSaisie(correct, texte, { ...opts(), suffixe: unit });
    payload._meta.generatorKey = 'lecture_tableau_mesures';
    return payload;
};

// Relier
const genRelier = () => {
    // Refonte bilan prof D3 (2026-07-27) : gabarit 2 sources / 5 cibles —
    // l'ancien 4 ↔ 4 offrait la dernière paire sans lire le tableau. Leurres :
    //   · mauvaise_colonne (un par source) : la valeur de la BONNE ligne mais
    //     de l'autre colonne — la réponse exacte de l'élève qui trouve la
    //     bonne entrée mais ne verrouille pas la colonne ;
    //   · mauvaise_ligne : bonne colonne, ligne voisine non demandée — erreur
    //     peu probable (les lignes sont nommées), gardée dans le doute.
    // ⚠️ Tout leurre s'affiche avec l'unité de la grandeur DEMANDÉE (l'élève
    // qui lit 3 dans la colonne Précipitations répond « 3 °C » — en « mm »,
    // l'unité trahirait le leurre par la forme). Toutes valeurs distinctes,
    // leurres compris : en cas de collision le leurre fautif est retiré.
    const ctx = buildContext();
    const table = tableHTML(ctx);
    const colIdx = pickCol(ctx);
    const colAutre = ctx.col_headers.length > 1 ? (colIdx === 0 ? 1 : 0) : null;
    const { grandeur, genre } = getGrandeur(ctx, colIdx);
    const unit = ctx.units[colIdx];

    const melange = shuffle([...ctx.rows]);
    const selected = [];
    const ciblesVues = new Set();
    for (const r of melange) {
        const cible = `${r.vals[colIdx]} ${unit}`;
        if (ciblesVues.has(cible)) continue;
        ciblesVues.add(cible);
        selected.push(r);
        if (selected.length === 2) break;
    }

    const pairs = selected.map(r => ({
        source: r.row,
        target: `${r.vals[colIdx]} ${unit}`,
    }));

    const prises = new Set(pairs.map(p => p.target));
    const leurres = [];
    const pushLeurre = (valeur, code) => {
        const txt = `${valeur} ${unit}`;
        if (prises.has(txt)) return;
        prises.add(txt);
        leurres.push({ target: txt, code });
    };
    if (colAutre !== null) {
        selected.forEach(r => pushLeurre(r.vals[colAutre], 'mauvaise_colonne'));
    }
    const voisine = melange.find(r => !selected.includes(r));
    if (voisine) pushLeurre(voisine.vals[colIdx], 'mauvaise_ligne');

    // L'avertissement ne s'affiche que s'il y a réellement des leurres
    // (décision utilisateur 2026-08-08 : sur le rare tirage 2 ↔ 2, la
    // phrase était mensongère).
    const avertissement = leurres.length
        ? ' Attention : certaines valeurs proposées ne correspondent à aucune réponse.'
        : '';
    const texte = `${intro(ctx)}${table}`
        + `<p>Relie chaque ${ctx.row_header.toLowerCase()} à ${sonSa(genre)} ${grandeur}.${avertissement}</p>`;
    const payload = makeRelier(pairs, texte, { ...opts(), cibles_leurres: leurres });
    payload._meta.generatorKey = 'lecture_tableau_mesures';
    payload._meta.params = {
        colonne: ctx.col_headers[colIdx],
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// Tableau V/F
const genVF = () => {
    const ctx = buildContext();
    const table = tableHTML(ctx);
    const colIdx = pickCol(ctx);
    const { grandeur, genre } = getGrandeur(ctx, colIdx);
    const unit = ctx.units[colIdx];

    const best = maxRow(ctx, colIdx);
    const worst = minRow(ctx, colIdx);
    // Unicité des extremums (revue 2026-08-08) : « X a LA plus grande… » sur
    // une colonne à ex æquo (Mardi et Vendredi à 15 mm) donnait une étiquette
    // contestable — un élève répondant Faux avait un argument légitime.
    const vals = ctx.rows.map(r => r.vals[colIdx]);
    const maxEstUnique = vals.filter(v => v === best.vals[colIdx]).length === 1;
    const minEstUnique = vals.filter(v => v === worst.vals[colIdx]).length === 1;

    // Clés sémantiques (revue 2026-08-08, recette gen 675) : cellule /
    // extremum / paire comparée. La sélection impose des clés toutes
    // distinctes — avant, 60,2 % des tirages avaient deux affirmations sur le
    // même objet, l'une donnant l'autre.
    // Pool de VRAI
    const vraiPool = [];
    // Lecture correcte
    ctx.rows.forEach(r => {
        vraiPool.push({ l: `${articleDef(genre)} ${grandeur} de ${r.row} est ${r.vals[colIdx]} ${unit}.`, k: `c:${r.row}` });
    });
    // Max/min correct
    if (maxEstUnique) {
        vraiPool.push({ l: `${best.row} a ${genre === 'm' ? 'le plus grand' : 'la plus grande'} ${grandeur} avec ${best.vals[colIdx]} ${unit}.`, k: 'ext:max' });
    }
    if (minEstUnique && best.row !== worst.row) {
        vraiPool.push({ l: `${worst.row} a la plus petite ${grandeur} avec ${worst.vals[colIdx]} ${unit}.`, k: 'ext:min' });
    }
    // Comparaison correcte
    if (ctx.rows.length >= 2) {
        const [a, b] = randomChoiceMultiple(ctx.rows, 2);
        const kComp = `comp:${[a.row, b.row].sort().join('|')}`;
        if (a.vals[colIdx] > b.vals[colIdx]) {
            vraiPool.push({ l: `${articleDef(genre)} ${grandeur} de ${a.row} est ${genre === 'm' ? 'supérieur' : 'supérieure'} à ${celuiCelle(genre)} de ${b.row}.`, k: kComp });
        } else if (a.vals[colIdx] < b.vals[colIdx]) {
            vraiPool.push({ l: `${articleDef(genre)} ${grandeur} de ${a.row} est ${genre === 'm' ? 'inférieur' : 'inférieure'} à ${celuiCelle(genre)} de ${b.row}.`, k: kComp });
        }
    }

    // Pool de FAUX
    const fauxPool = [];
    // Valeurs erronées
    ctx.rows.forEach(r => {
        const wrongVal = r.vals[colIdx] + randomChoice([-3, -2, -1, 1, 2, 3]);
        if (wrongVal >= 0) {
            fauxPool.push({ l: `${articleDef(genre)} ${grandeur} de ${r.row} est ${wrongVal} ${unit}.`, k: `c:${r.row}` });
        }
    });
    // Max/min inversé (les fausses restent solides même en cas d'ex æquo :
    // la ligne nommée n'est jamais parmi les gagnants)
    if (best.row !== worst.row) {
        fauxPool.push({ l: `${worst.row} a ${genre === 'm' ? 'le plus grand' : 'la plus grande'} ${grandeur}.`, k: 'ext:max' });
        fauxPool.push({ l: `${best.row} a la plus petite ${grandeur}.`, k: 'ext:min' });
    }
    // Comparaison fausse
    if (ctx.rows.length >= 2) {
        const [a, b] = randomChoiceMultiple(ctx.rows, 2);
        const kComp = `comp:${[a.row, b.row].sort().join('|')}`;
        if (a.vals[colIdx] > b.vals[colIdx]) {
            fauxPool.push({ l: `${articleDef(genre)} ${grandeur} de ${a.row} est ${genre === 'm' ? 'inférieur' : 'inférieure'} à ${celuiCelle(genre)} de ${b.row}.`, k: kComp });
        } else if (a.vals[colIdx] < b.vals[colIdx]) {
            fauxPool.push({ l: `${articleDef(genre)} ${grandeur} de ${a.row} est ${genre === 'm' ? 'supérieur' : 'supérieure'} à ${celuiCelle(genre)} de ${b.row}.`, k: kComp });
        }
    }

    // Sélection avec contrainte min 2V + 2F, clés toutes distinctes
    const nbVrais = randInt(2, 3);
    const nbFaux = 5 - nbVrais;

    const seenKeys = new Set();
    const pick = (pool, n) => {
        const out = [];
        for (const e of shuffle(pool)) {
            if (out.length >= n) break;
            if (!seenKeys.has(e.k)) {
                seenKeys.add(e.k);
                out.push(e.l);
            }
        }
        return out;
    };
    const selectedVrais = pick(vraiPool, nbVrais);
    const selectedFaux = pick(fauxPool, nbFaux);

    if (selectedVrais.length < nbVrais || selectedFaux.length < nbFaux) {
        // Retry on collision
        return genVF();
    }

    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);

    const lignes = mixed.map(m => m.l);
    const bonnesReponses = mixed.map(m => m.r);

    const texte = `${intro(ctx)}${table}<p>Indique si chaque affirmation est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts());
    payload._meta.generatorKey = 'lecture_tableau_mesures';
    return payload;
};

// Texte à trous (drag)
const genTrouDrag = () => {
    const ctx = buildContext();
    const table = tableHTML(ctx);
    const colIdx = pickCol(ctx);
    const { grandeur, genre } = getGrandeur(ctx, colIdx);
    const unit = ctx.units[colIdx];
    const modele = randomChoice(['max', 'difference']);

    if (modele === 'max') {
        // Extremum unique garanti (revue 2026-08-08) : en cas d'ex æquo,
        // l'autre gagnant était servi en distracteur et compté faux (141
        // tirages sur 6000).
        let c2 = ctx;
        let ci2 = colIdx;
        const maxUnique = (cc, ci) => {
            const vs = cc.rows.map(r => r.vals[ci]);
            return vs.filter(v => v === Math.max(...vs)).length === 1;
        };
        for (let tries = 0; !maxUnique(c2, ci2) && tries < 50; tries++) {
            c2 = buildContext();
            ci2 = pickCol(c2);
        }
        if (!maxUnique(c2, ci2)) return genTrouDrag();
        const table2 = tableHTML(c2);
        const { grandeur: g2, genre: genre2 } = getGrandeur(c2, ci2);
        const unit2 = c2.units[ci2];
        const best = maxRow(c2, ci2);
        const others = c2.rows.filter(r => r !== best);
        const distracteurs = [
            ...others.map(r => r.row),
            ...others.map(r => String(r.vals[ci2])),
        ];
        const phrase = `${articleDef(genre2)} ${g2} ${plusEleve(genre2)} est ${observe(genre2)} pour {{${best.row}}} avec une valeur de {{${best.vals[ci2]}}} ${unit2}.`;
        const texte = `${intro(c2)}${table2}<p>Complète la phrase en glissant les éléments proposés.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, shuffle(distracteurs)).slice(0, 4) });
        payload._meta.generatorKey = 'lecture_tableau_mesures';
        return payload;
    }

    // difference — refonte (revue 2026-08-08, formulation utilisateur).
    // Avant : « Entre Gabriel et Chloé, la distance a augmenté de 675 m »
    // (61,8 % des tirages hors contexte temporel), ordre à rebours du temps
    // (586/6000) et « augmenté de 0 » sur valeurs égales (107/6000).
    const temporel = ctx.row_header === 'Jour' || ctx.row_header === 'Période';
    let i1;
    let i2;
    let tries = 0;
    do {
        const idx = shuffle(ctx.rows.map((_, k) => k)).slice(0, 2);
        if (temporel) idx.sort((a, b) => a - b); // chronologie garantie
        [i1, i2] = idx;
        tries++;
    } while (ctx.rows[i1].vals[colIdx] === ctx.rows[i2].vals[colIdx] && tries < 30);
    if (ctx.rows[i1].vals[colIdx] === ctx.rows[i2].vals[colIdx]) return genTrouDrag();
    const item1 = ctx.rows[i1];
    const item2 = ctx.rows[i2];
    const v1 = item1.vals[colIdx];
    const v2 = item2.vals[colIdx];
    const diff = Math.abs(v1 - v2);
    const wrongDiff = Math.abs(diff + randomChoice([-2, -1, 1, 2, 3]));

    if (temporel) {
        // Évolution dans le temps : augmenté/diminué est légitime
        const verb = v2 > v1 ? 'augmenté' : 'diminué';
        const inverseVerb = v2 > v1 ? 'diminué' : 'augmenté';
        const distracteurs = [inverseVerb, String(wrongDiff), String(v1), String(v2)];
        const phrase = `Entre ${item1.row} et ${item2.row}, ${articleDefMin(genre)} ${grandeur} a {{${verb}}} de {{${diff}}} ${unit}.`;
        const texte = `${intro(ctx)}${table}<p>Complète la phrase en glissant les éléments proposés.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, shuffle(distracteurs)).slice(0, 4) });
        payload._meta.generatorKey = 'lecture_tableau_mesures';
        return payload;
    }

    // Deux mesures distinctes : un écart, pas une évolution
    const colAutre = ctx.col_headers.length > 1 ? (colIdx === 0 ? 1 : 0) : null;
    const ecartAutre = colAutre !== null ? Math.abs(item1.vals[colAutre] - item2.vals[colAutre]) : null;
    const distracteurs = [String(v1), String(v2), String(wrongDiff), ...(ecartAutre !== null ? [String(ecartAutre)] : [])];
    const phrase = `Entre ${item1.row} et ${item2.row}, l'écart de ${grandeur} est de {{${diff}}} ${unit}.`;
    const texte = `${intro(ctx)}${table}<p>Complète la phrase en glissant les éléments proposés.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'drag', distracteurs: filtrerDistracteurs(phrase, shuffle(distracteurs)).slice(0, 4) });
    payload._meta.generatorKey = 'lecture_tableau_mesures';
    return payload;
};

// Texte à trous (saisie)
const genTrouSaisie = () => {
    const ctx = buildContext();
    const table = tableHTML(ctx);
    const colIdx = pickCol(ctx);
    const { grandeur, genre } = getGrandeur(ctx, colIdx);
    const unit = ctx.units[colIdx];
    const modele = randomChoice(['lecture', 'difference']);

    if (modele === 'lecture') {
        const target = randomChoice(ctx.rows);
        const phrase = `${articleDef(genre)} ${grandeur} de ${target.row} est {{${target.vals[colIdx]}}} ${unit}.`;
        const texte = `${intro(ctx)}${table}<p>Complète la phrase avec la bonne valeur.</p>`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'saisie' });
        payload._meta.generatorKey = 'lecture_tableau_mesures';
        return payload;
    }

    // difference
    const [item1, item2] = randomChoiceMultiple(ctx.rows, 2);
    const diff = Math.abs(item1.vals[colIdx] - item2.vals[colIdx]);
    const phrase = `La différence de ${grandeur} entre ${item1.row} et ${item2.row} est {{${diff}}} ${unit}.`;
    const texte = `${intro(ctx)}${table}<p>Complète la phrase avec la bonne valeur.</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(), mode: 'saisie' });
    payload._meta.generatorKey = 'lecture_tableau_mesures';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────
const GENERATORS = {
    lecture_tableau_mesures_qcm_unique: genQcm,
    lecture_tableau_mesures_texte: genSaisie,
    lecture_tableau_mesures_relier: genRelier,
    lecture_tableau_mesures_tableau_vf: genVF,
    lecture_tableau_mesures_texte_trou_drag: genTrouDrag,
    lecture_tableau_mesures_texte_trou_saisie: genTrouSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    texte: 'Saisie',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'lecture_tableau_mesures',
        label: 'Lecture et exploitation d\'un tableau de mesures',
        formatsList: buildFormats('lecture_tableau_mesures'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du FORMAT résolu, TOUJOURS (pas celle de la carte posée par les
    // sous-générateurs) : c est elle que la carte d exerciseur compare pour
    // ses compteurs et que la réutilisation range (bug des compteurs muets
    // avant tout clic sur un onglet, tranché 2026-09-02).
    payload._meta.generatorKey = key;
    return payload;
};
